import React from "react";
import { Play, Pause, Volume2, Maximize2, ScanLine, Radar } from "lucide-react";
import SkeletalHudMock from "../components/landing/SkeletalHudMock";
import { ProRadar, Readout } from "../components/engine/ProScoutCharts";
import { ElitePlayerCard } from "../components/engine/ElitePlayerCard";
import { DEMO_PLAYER, DEMO_ENGINE } from "../components/landing/LabShowcase";

const GOLD = "#F5C518";

// Desktop split-screen demo: video proof (left) vs. Technical Engine analysis (right). Used for the widescreen tour film.
export default function SplitDemo() {
  return (
    <div data-testid="split-demo" className="min-h-screen elite-carbon-bg text-white overflow-hidden" style={{ fontFamily: "Montserrat, sans-serif" }}>
      <div className="absolute inset-0 elite-mesh-overlay pointer-events-none" />
      <div className="relative max-w-[1240px] mx-auto px-6 pt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img loading="lazy" decoding="async" src="/nexto-logo.png" alt="" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.35em] uppercase text-[#D4AF37]">Next O Technical Engine · Mission Control</div>
              <div className="font-headings text-xl uppercase tracking-wide leading-none">Marcus Reed · Elastico · Scan 07</div>
            </div>
          </div>
          <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] uppercase text-[#F5C518]"><span className="w-1.5 h-1.5 rounded-full bg-[#F5C518] animate-pulse" /> System_Active</div>
        </div>

        <div className="grid grid-cols-[1.15fr_1fr] gap-5 items-start">
          {/* Video proof */}
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}>
            <div className="relative" style={{ aspectRatio: "16 / 10" }}>
              <img loading="lazy" decoding="async" src="/marketing/action-dribble.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "55% 25%", filter: "contrast(1.2) saturate(0.9)" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30" />
              {[["top-3 left-3", "border-t-2 border-l-2"], ["top-3 right-3", "border-t-2 border-r-2"], ["bottom-14 left-3", "border-b-2 border-l-2"], ["bottom-14 right-3", "border-b-2 border-r-2"]].map(([p, b]) => <span key={p} className={`absolute w-8 h-8 ${p} ${b} border-[#F5C518]`} />)}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-white/85 bg-black/60 px-3 py-1.5 rounded-sm"><span className="w-2 h-2 rounded-full bg-[#C41E24] animate-pulse" /> Video Proof · Freeze at 0:07.4</div>
              <div className="absolute left-[54%] top-[58%] -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full border-2 border-[#F5C518]" style={{ boxShadow: `0 0 24px ${GOLD}` }} />
              <div className="absolute inset-x-0 bottom-0 px-4 pb-3 pt-6">
                <div className="h-1 rounded-full bg-white/20 overflow-hidden"><div className="h-full w-[46%] bg-gradient-to-r from-[#C41E24] to-[#F5C518]" /></div>
                <div className="mt-2 flex items-center gap-3 text-white/80">
                  <Pause size={16} /><Play size={16} className="text-white/40" /><Volume2 size={16} />
                  <span className="font-mono text-[10px] tracking-widest">0:07 / 0:15 · Slow-Mo x0.33</span>
                  <Maximize2 size={15} className="ml-auto" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 p-4 border-t border-white/10">
              <Readout k="Balance" v={DEMO_ENGINE.balance} /><Readout k="Execution" v={DEMO_ENGINE.execution} /><Readout k="Game Pace" v={DEMO_ENGINE.pace} />
            </div>
          </div>

          {/* Engine analysis */}
          <div className="grid grid-cols-[0.9fr_1.1fr] gap-4">
            <div>
              <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2"><ScanLine size={12} /> Skeletal HUD</div>
              <SkeletalHudMock compact="header" />
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2"><Radar size={12} /> Pro-Scout Radar</div>
                <ProRadar engine={DEMO_ENGINE} />
              </div>
              <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#0b0b0b]/80 p-3 elite-card-halo">
                <ElitePlayerCard player={DEMO_PLAYER} engine={DEMO_ENGINE} drill="Elastico · Scan 07" api="" animated cardOnly />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
